import api from './api';
import { normalizeLanguage } from '../constants/Languages';
import { ConsentFormStatus } from '../constants/ConsentForms';

export {
  ConsentContextType,
  ConsentFormStatus,
  MAX_CONSENT_FORM_FILE_BYTES,
  MAX_CONSENT_FORM_VALIDITY_MONTHS,
  isConsentFormActive,
} from '../constants/ConsentForms';

const withManagementStore = (storeId, extra = {}) => ({
  _authContext: 'management',
  _storeId: storeId,
  ...extra,
});

const withCustomerStore = (storeId, extra = {}) => ({
  _authContext: 'customer',
  _storeId: storeId,
  ...extra,
});

const normalizeLocalizedDictionary = (value) => {
  if (!value || typeof value !== 'object') {
    return {};
  }

  return Object.entries(value).reduce((accumulator, [key, entryValue]) => {
    if (typeof entryValue === 'string') {
      accumulator[normalizeLanguage(key)] = entryValue;
    }
    return accumulator;
  }, {});
};

const buildLocalizedPayload = (value) => (
  Object.fromEntries(
    Object.entries(normalizeLocalizedDictionary(value))
      .filter(([, entryValue]) => entryValue.trim())
      .map(([language, entryValue]) => [language.toUpperCase(), entryValue.trim()])
  )
);

const normalizeServiceIds = (serviceIds) => (
  Array.isArray(serviceIds)
    ? serviceIds.map((serviceId) => Number.parseInt(serviceId, 10)).filter((serviceId) => Number.isInteger(serviceId) && serviceId > 0)
    : []
);

const normalizeVersion = (version) => (version ? {
  id: version.id,
  versionNumber: Number(version.versionNumber) || 0,
  fileName: version.fileName || '',
  fileSizeBytes: Number(version.fileSizeBytes) || 0,
  sha256: version.sha256 || '',
  requiresReacceptance: Boolean(version.requiresReacceptance),
  createdAtUtc: version.createdAtUtc ?? null,
} : null);

export const normalizeConsentForm = (form) => ({
  id: form?.id ?? null,
  name: normalizeLocalizedDictionary(form?.name),
  validityMonths: form?.validityMonths ?? null,
  status: form?.status || ConsentFormStatus.Active,
  currentVersion: normalizeVersion(form?.currentVersion),
  versions: (form?.versions || []).map(normalizeVersion),
  serviceIds: normalizeServiceIds(form?.serviceIds),
  createdAtUtc: form?.createdAtUtc ?? null,
  updatedAtUtc: form?.updatedAtUtc ?? null,
});

export const normalizePendingConsentForm = (form) => ({
  consentFormId: form?.consentFormId ?? null,
  name: normalizeLocalizedDictionary(form?.name),
  versionId: form?.versionId ?? null,
  versionNumber: Number(form?.versionNumber) || 0,
  fileSizeBytes: Number(form?.fileSizeBytes) || 0,
  serviceIds: normalizeServiceIds(form?.serviceIds),
});

const normalizeAcceptedConsentForm = (form) => ({
  consentFormId: form?.consentFormId ?? null,
  name: normalizeLocalizedDictionary(form?.name),
  acceptanceId: form?.acceptanceId ?? null,
  versionNumber: Number(form?.versionNumber) || 0,
  acceptedAtUtc: form?.acceptedAtUtc ?? null,
});

const normalizeConsentStatus = (status) => ({
  pending: (status?.pending || []).map(normalizePendingConsentForm),
  accepted: (status?.accepted || []).map(normalizeAcceptedConsentForm),
});

const normalizeAcceptance = (acceptance) => ({
  id: acceptance?.id ?? null,
  consentFormId: acceptance?.consentFormId ?? null,
  formName: normalizeLocalizedDictionary(acceptance?.formName),
  versionNumber: Number(acceptance?.versionNumber) || 0,
  sha256: acceptance?.sha256 || '',
  acceptedAtUtc: acceptance?.acceptedAtUtc ?? null,
  contextType: acceptance?.contextType ?? null,
  serviceId: acceptance?.serviceId ?? null,
  appointmentId: acceptance?.appointmentId ?? null,
  appointmentDate: acceptance?.appointmentDate ?? null,
  collectionChannel: acceptance?.collectionChannel ?? null,
  language: acceptance?.language ?? null,
});

const normalizeMyPendingItem = (item) => ({
  contextType: item?.contextType ?? null,
  appointmentId: item?.appointmentId ?? null,
  appointmentDate: item?.appointmentDate ?? null,
  appointmentStartTime: item?.appointmentStartTime ?? null,
  customerServicePackageId: item?.customerServicePackageId ?? null,
  customerMembershipId: item?.customerMembershipId ?? null,
  itemName: normalizeLocalizedDictionary(item?.itemName),
  forms: (item?.forms || []).map(normalizePendingConsentForm),
});

const buildRequirementsPayload = ({ serviceIds, serviceComboIds, servicePackageId, membershipPlanId } = {}) => ({
  serviceIds: normalizeServiceIds(serviceIds),
  serviceComboIds: normalizeServiceIds(serviceComboIds),
  servicePackageId: servicePackageId || null,
  membershipPlanId: membershipPlanId || null,
});

export const buildConsentFormPayload = (form) => {
  const validityMonths = Number.parseInt(form?.validityMonths, 10);
  return {
    name: buildLocalizedPayload(form?.name),
    validityMonths: Number.isInteger(validityMonths) && validityMonths > 0 ? validityMonths : null,
  };
};


const ConsentFormService = {
  getConsentForms: async (storeId) => {
    const response = await api.get('/consent-forms', withManagementStore(storeId));
    return (response.data || []).map(normalizeConsentForm);
  },

  createConsentForm: async (storeId, form) => {
    const response = await api.post('/consent-forms', buildConsentFormPayload(form), withManagementStore(storeId));
    return normalizeConsentForm(response.data);
  },

  updateConsentForm: async (storeId, consentFormId, form) => {
    const response = await api.put(`/consent-forms/${consentFormId}`, buildConsentFormPayload(form), withManagementStore(storeId));
    return normalizeConsentForm(response.data);
  },

  uploadConsentFormVersion: async (storeId, consentFormId, file, requiresReacceptance) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('requiresReacceptance', requiresReacceptance ? 'true' : 'false');

    const response = await api.post(
      `/consent-forms/${consentFormId}/versions`,
      formData,
      withManagementStore(storeId, { headers: { 'Content-Type': 'multipart/form-data' } }),
    );
    return normalizeConsentForm(response.data);
  },

  archiveConsentForm: async (storeId, consentFormId) => {
    await api.post(`/consent-forms/${consentFormId}/archive`, null, withManagementStore(storeId));
  },

  restoreConsentForm: async (storeId, consentFormId) => {
    await api.post(`/consent-forms/${consentFormId}/restore`, null, withManagementStore(storeId));
  },

  getManagementConsentFormFile: async (storeId, versionId) => {
    const response = await api.get(
      `/consent-forms/versions/${versionId}/file`,
      withManagementStore(storeId, { responseType: 'blob' }),
    );
    return response.data;
  },

  getManagementRequirements: async (storeId, { customerId, ...scope }) => {
    const response = await api.post(
      '/consent-forms/management/requirements',
      { customerId, ...buildRequirementsPayload(scope) },
      withManagementStore(storeId),
    );
    return (response.data || []).map(normalizePendingConsentForm);
  },

  getAppointmentConsentStatus: async (storeId, appointmentId) => {
    const response = await api.get(`/consent-forms/appointments/${appointmentId}/status`, withManagementStore(storeId));
    return normalizeConsentStatus(response.data);
  },

  sendConsentRequest: async (storeId, { customerId, appointmentId }) => {
    const response = await api.post(
      '/consent-forms/requests',
      { customerId, appointmentId: appointmentId || null },
      withManagementStore(storeId),
    );
    return {
      pendingCount: Number(response.data?.pendingCount) || 0,
      emailSent: Boolean(response.data?.emailSent),
      smsSent: Boolean(response.data?.smsSent),
    };
  },

  getCustomerAcceptances: async (storeId, customerId) => {
    const response = await api.get(`/consent-forms/customers/${customerId}/acceptances`, withManagementStore(storeId));
    return (response.data || []).map(normalizeAcceptance);
  },

  downloadAcceptanceCertificate: async (storeId, acceptanceId) => {
    const response = await api.get(
      `/consent-forms/acceptances/${acceptanceId}/certificate`,
      withManagementStore(storeId, { responseType: 'blob' }),
    );
    return response.data;
  },

  getConsentRequirements: async (storeId, scope) => {
    const response = await api.post('/consent-forms/requirements', buildRequirementsPayload(scope), withCustomerStore(storeId));
    return (response.data || []).map(normalizePendingConsentForm);
  },

  getCustomerConsentFormFile: async (storeId, versionId) => {
    const response = await api.get(
      `/consent-forms/versions/${versionId}/file`,
      withCustomerStore(storeId, { responseType: 'blob' }),
    );
    return response.data;
  },

  acceptConsentForms: async (storeId, { contextType, contextItemId, appointmentId, language, items }) => {
    const response = await api.post(
      '/consent-forms/accept',
      {
        contextType,
        contextItemId: contextItemId || null,
        appointmentId: appointmentId || null,
        language: String(normalizeLanguage(language) || 'en').toUpperCase(),
        items: items.map((item) => ({
          versionId: item.versionId,
          serviceId: item.serviceId ?? null,
          viewerOpenedAtUtc: item.viewerOpenedAtUtc,
          scrolledToEndAtUtc: item.scrolledToEndAtUtc,
        })),
      },
      withCustomerStore(storeId),
    );
    return {
      acceptanceBatchId: response.data?.acceptanceBatchId ?? null,
      acceptedCount: Number(response.data?.acceptedCount) || 0,
    };
  },

  getMyPendingConsents: async (storeId) => {
    const response = await api.get('/consent-forms/my-pending', withCustomerStore(storeId));
    return (response.data || []).map(normalizeMyPendingItem);
  },
};

export default ConsentFormService;
